import { Component, OnInit, Input, ViewChild, ElementRef, AfterViewInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { HotToastService } from "@ngneat/hot-toast";
import {
  UnsplashImageService,
  UnsplashImage,
  UnsplashSearchResponse,
} from "../../services/unsplash-image.service";
import {
  PexelsImageService,
  PexelsPhoto,
  PexelsSearchResponse,
} from "../../services/pexels-image.service";
import { HorizontalTextInputComponent } from "../horizontal-text-input/horizontal-text-input.component";
import { SelectComponent } from "../select/select.component";
import { ButtonComponent } from "../button/button.component";
import { ModalComponent } from "../../../components/layout/modal/modal.component";

// Unified interface for both image types
interface UnifiedImage {
  id: string | number;
  urls: {
    small: string;
    regular: string;
    full: string;
  };
  photographer: string;
  photographerUrl: string;
  alt: string;
  downloadLocation?: string;
}

interface SearchParams {
  query: string;
  page: number;
  perPage: number;
  orientation?: string;
}

interface SearchResult {
  images: UnifiedImage[];
  totalPages: number;
  total: number;
}

@Component({
  selector: "app-image-modal",
  templateUrl: "./image-modal.component.html",
  styleUrls: ["./image-modal.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    HorizontalTextInputComponent,
    SelectComponent,
    ButtonComponent,
    ModalComponent,
  ],
})
export class ImageModalComponent implements OnInit, AfterViewInit {
  @Input() query: string = "";
  @Input() orientation: "landscape" | "portrait" | "squarish" | "square" | undefined = undefined;
  @Input() perPage: number = 12;
  @Input() randomSearch: boolean = false;
  @Input() imageProvider: "unsplash" | "pexels" = "unsplash";

  @ViewChild("searchInput") searchInput!: ElementRef<HTMLInputElement>;

  searchForm: FormGroup;
  images: UnifiedImage[] = [];
  selectedImage: UnifiedImage | null = null;
  loading: boolean = false;
  loadingMore: boolean = false;
  currentPage: number = 1;
  totalPages: number = 0;
  total: number = 0;
  hasSearched: boolean = false;

  orientationOptions = [
    { value: "", label: "Alle oriëntaties" },
    { value: "landscape", label: "Liggend" },
    { value: "portrait", label: "Staand" },
    { value: "squarish", label: "Vierkant" },
  ];

  pageSizeOptions = [
    { value: "12", label: "12 per pagina" },
    { value: "24", label: "24 per pagina" },
    { value: "30", label: "30 per pagina" },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private unsplashService: UnsplashImageService,
    private pexelsService: PexelsImageService,
    private toast: HotToastService
  ) {
    this.searchForm = new FormGroup({
      query: new FormControl("", Validators.required),
      orientation: new FormControl(""),
      perPage: new FormControl("12"),
    });
  }

  ngOnInit(): void {
    this.searchForm.patchValue({
      query: this.query,
      orientation: this.orientation || "",
      perPage: this.perPage.toString(),
    });

    if (this.query) {
      this.search();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 0);
  }

  search(page: number = 1): void {
    const formValue = this.searchForm.value;

    if (!formValue.query) {
      this.toast.error("Voer een zoekterm in");
      return;
    }

    this.loading = true;
    this.hasSearched = true;
    this.currentPage = page;

    const params: SearchParams = {
      query: formValue.query,
      page,
      perPage: parseInt(formValue.perPage) || 12,
      orientation: formValue.orientation || undefined,
    };

    this.performSearch(params, false);
  }

  loadMore(): void {
    const nextPage = this.currentPage + 1;

    if (nextPage > this.totalPages) {
      return;
    }

    if (this.randomSearch) {
      this.toast.info("Willekeurige zoekresultaten ondersteunen geen paginering");
      return;
    }

    this.loadingMore = true;

    const params: SearchParams = {
      query: this.searchForm.value.query,
      page: nextPage,
      perPage: parseInt(this.searchForm.value.perPage) || 12,
      orientation: this.searchForm.value.orientation || undefined,
    };

    this.performSearch(params, true);
  }

  private performSearch(params: SearchParams, appendResults: boolean): void {
    const normalizedOrientation = this.normalizeOrientation(params.orientation);

    if (this.imageProvider === "pexels") {
      this.pexelsService
        .searchPhotos(
          params.query,
          params.page,
          params.perPage,
          normalizedOrientation as any,
          "medium",
          undefined,
          "nl-NL"
        )
        .subscribe({
          next: (response: PexelsSearchResponse) => {
            const result: SearchResult = {
              images: response.photos.map((photo) => this.mapPexelsToUnified(photo)),
              totalPages: Math.ceil(response.total_results / params.perPage),
              total: response.total_results,
            };
            this.handleSearchSuccess(result, appendResults);
          },
          error: (error) => this.handleSearchError(error, appendResults),
        });
    } else {
      if (this.randomSearch) {
        this.unsplashService
          .getRandomPhoto(
            undefined,
            undefined,
            undefined,
            params.query,
            normalizedOrientation as any,
            undefined,
            params.perPage
          )
          .subscribe({
            next: (response: UnsplashImage | UnsplashImage[]) => {
              const unsplashImages = Array.isArray(response) ? response : [response];
              const result: SearchResult = {
                images: unsplashImages.map((img) => this.mapUnsplashToUnified(img)),
                totalPages: 1,
                total: unsplashImages.length,
              };
              this.handleSearchSuccess(result, appendResults);
            },
            error: (error) => this.handleSearchError(error, appendResults),
          });
      } else {
        this.unsplashService
          .searchPhotos(
            params.query,
            params.page,
            params.perPage,
            "relevant",
            undefined,
            undefined,
            undefined,
            normalizedOrientation as any
          )
          .subscribe({
            next: (response: UnsplashSearchResponse) => {
              const result: SearchResult = {
                images: response.results.map((img) => this.mapUnsplashToUnified(img)),
                totalPages: response.total_pages,
                total: response.total,
              };
              this.handleSearchSuccess(result, appendResults);
            },
            error: (error) => this.handleSearchError(error, appendResults),
          });
      }
    }
  }

  private handleSearchSuccess(result: SearchResult, appendResults: boolean): void {
    if (appendResults) {
      this.images = [...this.images, ...result.images];
      this.currentPage++;
      this.loadingMore = false;
    } else {
      this.images = result.images;
      this.loading = false;
    }

    this.totalPages = result.totalPages;
    this.total = result.total;

    if (result.images.length === 0 && !appendResults) {
      this.toast.warning("Geen afbeeldingen gevonden");
    }
  }

  private handleSearchError(error: any, appendResults: boolean): void {
    console.error("Error searching images:", error);
    const action = appendResults ? "laden van meer" : "zoeken van";
    this.toast.error(`Fout bij het ${action} afbeeldingen`);

    if (appendResults) {
      this.loadingMore = false;
    } else {
      this.loading = false;
    }
  }

  private normalizeOrientation(orientation: string | undefined): string | undefined {
    if (!orientation) return undefined;

    // Convert "squarish" to "square" for Pexels
    if (this.imageProvider === "pexels" && orientation === "squarish") {
      return "square";
    }

    return orientation;
  }

  private mapUnsplashToUnified(image: UnsplashImage): UnifiedImage {
    return {
      id: image.id,
      urls: {
        small: image.urls.small,
        regular: image.urls.regular,
        full: image.urls.full,
      },
      photographer: image.user.name,
      photographerUrl: `${image.user.links.html}?utm_source=AssistOnline&utm_medium=referral`,
      alt: image.alt_description || image.description || "Unsplash image",
      downloadLocation: image.links.download_location,
    };
  }

  private mapPexelsToUnified(photo: PexelsPhoto): UnifiedImage {
    return {
      id: photo.id,
      urls: {
        small: photo.src.small,
        regular: photo.src.medium,
        full: photo.src.original,
      },
      photographer: photo.photographer,
      photographerUrl: `${photo.photographer_url}?utm_source=AssistOnline&utm_medium=referral`,
      alt: photo.alt || "Pexels image",
    };
  }

  selectImage(image: UnifiedImage): void {
    this.selectedImage = image;
  }

  selectAndConfirm(image: UnifiedImage): void {
    this.selectedImage = image;
    this.confirmSelection();
  }

  confirmSelection(): void {
    if (!this.selectedImage) {
      this.toast.error("Selecteer een afbeelding");
      return;
    }

    if (this.imageProvider === "unsplash" && this.selectedImage.downloadLocation) {
      this.unsplashService.trackDownload(this.selectedImage.downloadLocation).subscribe();
    }

    this.activeModal.close(this.selectedImage.urls.full);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  getPhotographerUrl(image: UnifiedImage): string {
    return image.photographerUrl;
  }

  getProviderUrl(): string {
    return this.imageProvider === "pexels"
      ? "https://www.pexels.com/?utm_source=AssistOnline&utm_medium=referral"
      : "https://unsplash.com/?utm_source=AssistOnline&utm_medium=referral";
  }

  getProviderName(): string {
    return this.imageProvider === "pexels" ? "Pexels" : "Unsplash";
  }
}
